import { useMutation, useQuery } from '@apollo/client/react'
import { groupRequestTypes } from '@src/constants/group-request-types'
import {
  Mutation,
  Query,
  ScoSignupRequest,
  SubmitHubspotFormInput,
  Subscription,
  UpsertSignupRequestInput,
} from '@src/graphql-types'
import { useWindow } from '@src/hooks/useWindow'
import { gql } from '@src/lib/gql'
import { omit, pick } from 'lodash'
import { parseAsString, useQueryState } from 'nuqs'
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

const DEFAULT_SAVE_ON_UPDATE_DEBOUNCE_MS = 500

const SIGNUP_REQUEST_INPUT_KEYS: Array<keyof UpsertSignupRequestInput> = [
  'additionalFields',
  'bodyAsMarkdown',
  'companyElevatorPitch',
  'companyName',
  'convertedAt',
  'convertingSubscriptionId',
  'createdByIpAddress',
  'deletedAt',
  'fundingGoalInDollars',
  'funnelIdentifier',
  'groupRequestTypeKey',
  'investorIndustryTagTitles',
  'lastProcessedAt',
  'location',
  'locationCity',
  'locationCountryKey',
  'locationPlaceId',
  'locationPostalCode',
  'locationState',
  'nextProcessingAt',
  'pitchDeckUrl',
  'recommendationAsMarkdown',
  'recommendedFeaturesAsJson',
  'recommendedProductId',
  'reportInputAsJson',
  'reportOutputAsJson',
  'requestingEmail',
  'requestingFullName',
  'requestingUserId',
  'selectedProductId',
  'signupRequestId',
  'skillTagTitles',
  'tagTitles',
  'title',
]

const SIGNUP_REQUEST_FRAGMENT = gql`
  fragment _ on ScoSignupRequest {
    id
    additionalFields
    bodyAsMarkdown
    companyElevatorPitch
    companyName
    fundingGoalInDollars
    groupRequestId
    groupRequestTypeKey
    investorIndustryTagTitles
    pitchDeckUrl
    recommendationAsMarkdown
    recommendedFeaturesAsJson
    recommendedProductId
    requestingEmail
    requestingFullName
    requestingUserId
    selectedProductId
    signupRequestId
    skillTagTitles
    locationCity
    locationState
    createdByIpLocation
    funnelIdentifier
    title
    tagTitles
    investorIndustryTagTitles
    skillTagTitles
    location
    locationCity
    locationState
    locationPostalCode
    locationCountryKey
    reportOutputAsJson
    tags {
      id
      tagId
      tagTypeKey
      title
    }
  }
`

const SIGNUP_REQUEST_QUERY = gql`
  query ($signupRequestId: String) {
    scoSignupRequest(signupRequestId: $signupRequestId) {
      ${SIGNUP_REQUEST_FRAGMENT}
    }
  }
`

const UPSERT_SIGNUP_REQUEST_MUTATION = gql`
  mutation ($input: UpsertSignupRequestInput!) {
    upsertSignupRequest(input: $input) {
      signupRequest {
        ${SIGNUP_REQUEST_FRAGMENT}
      }
    }
  }
`

const SUBMIT_HUBSPOT_FORM_MUTATION = gql`
  mutation ($input: SubmitHubspotFormInput!) {
    submitHubspotForm(input: $input) {
      success
    }
  }
`

export type RequestFunnelContextValue = {
  clearSignupRequest: () => void
  groupRequestType: (typeof groupRequestTypes)[number] | null
  isHubspotSubmitting: boolean
  isLoading: boolean
  isSaving: boolean
  query: ReturnType<typeof useQuery<Query & Mutation & Subscription>>
  saveSignupRequest: (
    next?: ((signupRequest: Partial<ScoSignupRequest>) => any) | null,
    additionalInput?: Partial<UpsertSignupRequestInput>,
    syncToState?: boolean
  ) => Promise<void>
  setStep: (step: string) => void
  signupRequest: Partial<ScoSignupRequest>
  signupRequestId: string | null
  step: string | null
  submitHubspotForm: any | ((options: { variables: { input: SubmitHubspotFormInput } }) => any)
  updateSignupRequest: (values: Partial<ScoSignupRequest>, save?: boolean) => void
}

export const RequestFunnelContext = createContext<RequestFunnelContextValue>({
  clearSignupRequest: () => {},
  groupRequestType: null,
  isHubspotSubmitting: false,
  isLoading: false,
  isSaving: false,
  query: null,
  saveSignupRequest: async () => {},
  setStep: () => {},
  signupRequest: {},
  signupRequestId: null,
  step: null,
  submitHubspotForm: null,
  updateSignupRequest: () => {},
})

export const useRequestFunnelContext = () => useContext(RequestFunnelContext)

export const RequestFunnelContextProvider: FC<{
  children?: ReactNode
  funnelIdentifier?: string
  initialSignupRequest?: Partial<ScoSignupRequest>
  initialSignupRequestId?: string
  saveOnMount?: boolean
  saveOnStepChange?: boolean
  saveOnUpdateDebounceMs?: number
  saveOnUpdate?: boolean
}> = ({
  children,
  initialSignupRequestId,
  funnelIdentifier,
  initialSignupRequest,
  saveOnMount = false,
  saveOnStepChange = true,
  saveOnUpdateDebounceMs = DEFAULT_SAVE_ON_UPDATE_DEBOUNCE_MS,
  saveOnUpdate = true,
}) => {
  const [signupRequestId, setSignupRequestId] = useQueryState(
    'signupRequestId',
    parseAsString.withDefault(initialSignupRequestId || initialSignupRequest?.signupRequestId)
  )
  const [signupRequest, setSignupRequest] = useState<Partial<ScoSignupRequest>>({
    funnelIdentifier: funnelIdentifier || initialSignupRequest?.funnelIdentifier || null,
    signupRequestId: initialSignupRequest?.signupRequestId ?? initialSignupRequestId ?? signupRequestId,
    ...initialSignupRequest,
  })
  const [step, setStep] = useQueryState('step', parseAsString.withDefault('start'))
  const window = useWindow()
  const stepRef = useRef<string>(step)
  const signupRequestRef = useRef<Partial<ScoSignupRequest>>(signupRequest)
  const signupRequestIdRef = useRef<string | null>(signupRequestId)
  const signupRequestVersionRef = useRef(0)
  const saveOnUpdateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const query = useQuery(SIGNUP_REQUEST_QUERY, {
    skip: !signupRequestId,
    variables: { signupRequestId },
  })
  const queryRef = useRef(query)

  const [upsertSignupRequest, upsertSignupRequestMutation] = useMutation(UPSERT_SIGNUP_REQUEST_MUTATION)

  const [submitHubspotForm, submitHubspotFormMutation] = useMutation(SUBMIT_HUBSPOT_FORM_MUTATION)

  const clearSaveOnUpdateTimeout = useCallback(() => {
    if (saveOnUpdateTimeoutRef.current) {
      clearTimeout(saveOnUpdateTimeoutRef.current)
      saveOnUpdateTimeoutRef.current = null
    }
  }, [])

  const saveSignupRequest = useCallback(
    async (
      next: ((signupRequest: Partial<ScoSignupRequest>) => any) | null = null,
      additionalInput: Partial<UpsertSignupRequestInput> = {},
      syncToState: boolean = false
    ) => {
      clearSaveOnUpdateTimeout()

      let currentSignupRequest = signupRequestRef.current
      const currentSignupRequestId = signupRequestIdRef.current
      const saveStartedAtVersion = signupRequestVersionRef.current

      // If we have an existing id, prefer the latest server snapshot from Apollo's cache as the
      // baseline for the save payload. This ensures that even if a save (debounced, step-change,
      // or explicit) is triggered before the query effect has committed the hydrated data into
      // local state, we still send the full previous additionalFields (plus any local overlays).
      // Without this, the first save on resume could send a partial/empty additionalFields and
      // reset the server value.
      const serverFromCache = queryRef.current.data?.scoSignupRequest
      if (currentSignupRequestId && serverFromCache) {
        currentSignupRequest = {
          ...serverFromCache,
          ...currentSignupRequest,
        }
        if (currentSignupRequest.additionalFields || serverFromCache.additionalFields) {
          currentSignupRequest = {
            ...currentSignupRequest,
            additionalFields: {
              ...(serverFromCache.additionalFields || {}),
              ...(currentSignupRequest.additionalFields || {}),
            },
          }
        }
      }

      const input: UpsertSignupRequestInput = {
        ...(pick(currentSignupRequest, SIGNUP_REQUEST_INPUT_KEYS) as Partial<UpsertSignupRequestInput>),
        funnelIdentifier: funnelIdentifier || currentSignupRequest.funnelIdentifier,
        signupRequestId:
          currentSignupRequest.signupRequestId ?? currentSignupRequestId ?? initialSignupRequestId,
        ...additionalInput,
      }

      // Guest placeholder UUID is not a real users row — never send it (FK fails).
      // Also drop null so we do not wipe a server-side claim from registerUser.
      const GUEST_USER_ID = '00000000-0000-0000-0000-000000000000'
      if (
        input.requestingUserId == null ||
        input.requestingUserId === '' ||
        input.requestingUserId === GUEST_USER_ID
      ) {
        delete input.requestingUserId
      }

      const result = await upsertSignupRequest({
        variables: {
          input,
        },
      })

      const returned: Partial<ScoSignupRequest> = result.data?.upsertSignupRequest?.signupRequest
      if (returned) {
        if (returned.signupRequestId && signupRequestIdRef.current !== returned.signupRequestId) {
          signupRequestIdRef.current = returned.signupRequestId
          void setSignupRequestId(returned.signupRequestId)
        }

        if (signupRequestVersionRef.current === saveStartedAtVersion) {
          const nextSignupRequest = syncToState ? { ...signupRequestRef.current, ...returned } : returned
          signupRequestRef.current = nextSignupRequest
          setSignupRequest(nextSignupRequest)
        } else {
          setSignupRequest(prev => {
            const nextSignupRequest = {
              ...prev,
              groupRequestId: returned.groupRequestId ?? prev.groupRequestId,
              id: returned.id ?? prev.id,
              signupRequestId: returned.signupRequestId ?? prev.signupRequestId,
            }
            signupRequestRef.current = nextSignupRequest
            return nextSignupRequest
          })
        }
      }

      next?.(returned)
    },
    [
      clearSaveOnUpdateTimeout,
      funnelIdentifier,
      initialSignupRequestId,
      setSignupRequestId,
      upsertSignupRequest,
    ]
  )

  useEffect(() => {
    signupRequestRef.current = signupRequest
  }, [signupRequest])

  useEffect(() => {
    signupRequestIdRef.current = signupRequestId
  }, [signupRequestId])

  useEffect(() => {
    queryRef.current = query
  }, [query])

  useEffect(() => clearSaveOnUpdateTimeout, [clearSaveOnUpdateTimeout])

  useEffect(() => {
    if (saveOnMount && !signupRequestId) {
      void saveSignupRequest()
    }
  }, [saveOnMount, saveSignupRequest, signupRequestId])

  useEffect(() => {
    if (step && stepRef.current !== step) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      if (saveOnStepChange) {
        // On initial load/resume with an id, suppress the automatic step-change save until
        // the server data has arrived. This avoids a race where we upsert a pre-hydration
        // (partial additionalFields) snapshot. User-driven saves and explicit calls are still allowed.
        const hasExistingId = !!signupRequestIdRef.current
        const serverDataReady = !!queryRef.current.data?.scoSignupRequest
        if (!hasExistingId || serverDataReady) {
          void saveSignupRequest()
        }
      }
    }
    stepRef.current = step
  }, [saveOnStepChange, saveSignupRequest, step, window])

  useEffect(() => {
    if (initialSignupRequestId && !signupRequestId) {
      void setSignupRequestId(initialSignupRequestId)
    }
  }, [initialSignupRequestId, setSignupRequestId, signupRequestId])

  useEffect(() => {
    const serverData = query.data?.scoSignupRequest
    if (!serverData) {
      return
    }

    setSignupRequest(prev => {
      // Smart hydration merge:
      // - Server data is the baseline (ensures we get the full previous additionalFields etc. on first load/resume).
      // - Any local optimistic changes (including those made in the brief window before this effect runs,
      //   or from concurrent user actions) win, especially for additionalFields keys.
      // This prevents both (a) premature saves from sending truncated additionalFields, and
      // (b) later/out-of-order query cache updates from resetting user changes.
      const next: Partial<ScoSignupRequest> = { ...serverData }

      // additionalFields: deep-merge so missing keys are filled from server, but locally-set keys win.
      if (prev.additionalFields && typeof prev.additionalFields === 'object') {
        next.additionalFields = {
          ...(serverData.additionalFields || {}),
          ...prev.additionalFields,
        }
      }

      // If the user has already performed updates (version > 0), also preserve other locally-set fields.
      if (signupRequestVersionRef.current > 0) {
        for (const key of Object.keys(prev)) {
          if (key !== 'additionalFields' && prev[key as keyof ScoSignupRequest] != null) {
            ;(next as any)[key] = prev[key as keyof ScoSignupRequest]
          }
        }
      }

      signupRequestRef.current = next
      return next
    })
  }, [query.data?.scoSignupRequest])

  const scheduleSaveOnUpdate = useCallback(
    (delayMs = saveOnUpdateDebounceMs) => {
      clearSaveOnUpdateTimeout()
      saveOnUpdateTimeoutRef.current = setTimeout(
        () => {
          saveOnUpdateTimeoutRef.current = null
          void saveSignupRequest()
        },
        Math.max(0, delayMs)
      )
    },
    [clearSaveOnUpdateTimeout, saveOnUpdateDebounceMs, saveSignupRequest]
  )

  const updateSignupRequest = useCallback(
    (values: Partial<ScoSignupRequest>, save = false) => {
      signupRequestVersionRef.current += 1
      setSignupRequest(prev => {
        const newValues = { ...prev, ...omit(values, ['additionalFields']) }
        if (!newValues.additionalFields) {
          newValues.additionalFields = {}
        }
        for (const key of Object.keys(values.additionalFields || {})) {
          newValues.additionalFields = {
            ...newValues.additionalFields,
            [key]: values.additionalFields[key],
          }
        }

        signupRequestRef.current = newValues

        return newValues
      })

      if (save) {
        scheduleSaveOnUpdate(0)
      } else if (saveOnUpdate) {
        scheduleSaveOnUpdate()
      }
    },
    [saveOnUpdate, scheduleSaveOnUpdate]
  )

  const clearSignupRequest = useCallback(() => {
    const nextSignupRequest = { ...initialSignupRequest }
    signupRequestVersionRef.current += 1
    clearSaveOnUpdateTimeout()
    signupRequestRef.current = nextSignupRequest
    signupRequestIdRef.current = null
    setSignupRequest(nextSignupRequest)
    setSignupRequestId(null)
  }, [clearSaveOnUpdateTimeout, initialSignupRequest, setSignupRequestId])

  const groupRequestType = useMemo(
    () => groupRequestTypes.find(t => t.key === signupRequest.groupRequestTypeKey) ?? null,
    [signupRequest.groupRequestTypeKey]
  )

  const contextValue = useMemo<RequestFunnelContextValue>(
    () => ({
      clearSignupRequest,
      groupRequestType,
      isHubspotSubmitting: submitHubspotFormMutation.loading,
      isLoading: query.loading,
      isSaving: upsertSignupRequestMutation.loading,
      query,
      saveSignupRequest,
      setStep,
      signupRequest,
      signupRequestId,
      step,
      submitHubspotForm,
      updateSignupRequest,
    }),
    [
      clearSignupRequest,
      groupRequestType,
      query,
      saveSignupRequest,
      setStep,
      signupRequest,
      signupRequestId,
      step,
      submitHubspotForm,
      updateSignupRequest,
      submitHubspotFormMutation.loading,
      upsertSignupRequestMutation.loading,
    ]
  )

  return <RequestFunnelContext.Provider value={contextValue}>{children}</RequestFunnelContext.Provider>
}
